import { UniqueIdentifier } from '@dnd-kit/core';
import { useMemo } from 'react';
import { DemeterDashboardWidgetModel, DemeterDashboardWidgetPageElementModel, DemeterPageElementType } from '../../../Generated/Raven-Demeter';
import ProjectionChart from '../../Components/Charts/Projection/ProjectionChart';
import SeasonalChart from '../../Components/Charts/Seasonal/SeasonalChart';
import useDragAndDrop from '../../Components/DragAndDrop/Hooks/useDragAndDropHook';
import { ComponentHeaderRightSideOptions } from '../../Components/Form/Inputs/ComponentMoreOptionsHorizontalDots';
import useLanguage from '../../Services/Language/useLanguageHook';
import BalanceTableChart from '../BalanceTable/BalanceTableChart';
import OtcPricesChart from '../Futures/Otc/OtcPricesChart';
import FuturesPricesChart from '../Futures/Prices/FuturesPricesChart';
import FuturesPricesComparisonChart from '../Futures/Prices/FuturesPricesComparisonChart';
import FuturesPricesForwardCurveChart from '../Futures/Prices/FuturesPricesForwardCurveChart';
import PhysicalPricesChart from '../PhysicalPrices/PhysicalPricesChart';
import CustomDashboardCommoditySummaryTable from './CustomDefaultDashboardComponents/CustomDashboardCommoditySummaryTable';
import CustomDashboardHedgeMonitorProIndicators from './CustomDefaultDashboardComponents/CustomDashboardHedgeMonitorProIndicators';
import CustomDashboardIndicators from './CustomDefaultDashboardComponents/CustomDashboardIndicators';
import CustomDashboardMarketIndicators from './CustomDefaultDashboardComponents/CustomDashboardMarketIndicators';
import CustomDashboardMarketIntelligenceReports from './CustomDefaultDashboardComponents/CustomDashboardMarketIntelligenceReports';
import CustomDashboardPriceSummaries from './CustomDefaultDashboardComponents/CustomDashboardPriceSummaries';
import DashboardNonChartWrapper from './DashboardNonChartWrapper';
import { DashboardPricesTable } from './DashboardTables/DashboardTableFromPageElement';
import styles from './DragAndDropWrapper.module.scss';
import withBalanceTableChartFromPageElement from './HigherOrderComponents/withBalanceTableChartFromPageElement';
import withFuturesPricesChartFromPageElement from './HigherOrderComponents/withFuturesPricesChartFromPageElement';
import withFuturesPricesComparisonChartFromPageElement from './HigherOrderComponents/withFuturesPricesComparisonChartFromPageElement';
import withFuturesPricesForwardCurveChartFromPageElement from './HigherOrderComponents/withFuturesPricesForwardCurveChartFromPageElement';
import withFuturesPricesTableFromPageElement from './HigherOrderComponents/withFuturesPricesTableFromPageElement';
import withLeadingIndicatorsFromPageElement from './HigherOrderComponents/withLeadingIndicatorsFromPageElement';
import withMarginChartFromPageElement from './HigherOrderComponents/withMarginChartFromPageElement';
import withOtcPricesChartFromPageElement from './HigherOrderComponents/withOtcPricesChartFromPageElement';
import withOtcPricesTableFromPageElement from './HigherOrderComponents/withOtcPricesTableFromPageElement';
import withPhysicalPricesChartFromPageElement from './HigherOrderComponents/withPhysicalPricesChartFromPageElement';
import withPhysicalPricesTableFromPageElement from './HigherOrderComponents/withPhysicalPricesTableFromPageElement';
import withProjectionChartFromPageElement from './HigherOrderComponents/withProjectionChartFromPageElement';
import withSeasonalChartFromPageElement from './HigherOrderComponents/withSeasonalChartFromPageElement';

interface IGenerateComponentFromPageElementProps {
    dashboardWidget: DemeterDashboardWidgetModel;
    dashboardWidgetPageElement: DemeterDashboardWidgetPageElementModel;
    id: UniqueIdentifier;
    isInEditMode: boolean;
    handleMoreOptionsSelection: (value: ComponentHeaderRightSideOptions, dashboardWidget: DemeterDashboardWidgetPageElementModel) => void;
    maximizeMinimizeValue?: ComponentHeaderRightSideOptions.Maximize | ComponentHeaderRightSideOptions.Minimize;
}

// Charts should be allowed to "stretch" width-wise more since they may be set to full row width.
const chartMaxWidthHeightRatio = 6.0;
const allowedPageElementTypesForDynamicHeight: DemeterPageElementType[] = [
    DemeterPageElementType.LeadingIndicators,
    DemeterPageElementType.CommodityIndicators,
    DemeterPageElementType.CommoditySummaryTable,
    DemeterPageElementType.PriceSummaries,
    DemeterPageElementType.HedgeMonitorPro,
    DemeterPageElementType.FuturesPricesTable,
    DemeterPageElementType.OtcPricesTable,
    DemeterPageElementType.PhysicalPricesTable,
];
const GenerateComponentFromPageElement: React.FC<IGenerateComponentFromPageElementProps> = (props: IGenerateComponentFromPageElementProps) => {
    const [translations] = useLanguage();
    const dragAndDropContext = useDragAndDrop(props.id, props.isInEditMode);

    const handleMoreEditOptionsSelection = (value: ComponentHeaderRightSideOptions) => {
        if (!props.isInEditMode) {
            return;
        }

        props.handleMoreOptionsSelection(value, props.dashboardWidgetPageElement);
    };

    const shouldUseDynamicHeight = useMemo(
        () =>
            !props.isInEditMode &&
            props.dashboardWidgetPageElement.pageElement.pageElementType &&
            allowedPageElementTypesForDynamicHeight.includes(props.dashboardWidgetPageElement.pageElement.pageElementType),
        [(props.isInEditMode, props.dashboardWidgetPageElement.pageElement.pageType, props.dashboardWidgetPageElement.pageElement.pageElementType)],
    );

    const switchOnPageElementType = useMemo(() => {
        const baseComponentProps = {
            pageElement: props.dashboardWidgetPageElement.pageElement,
            customTitle: props.dashboardWidgetPageElement.options?.customTitle,
            dragIndicatorIcon: dragAndDropContext.dragIndicatorIcon,
            isInDashboardEditMode: props.isInEditMode,
            maximizeMinimizeButton: props.maximizeMinimizeValue,
            widgetOptions: props.dashboardWidgetPageElement.options,
            useDynamicHeight: shouldUseDynamicHeight,
            handleMoreOptionsSelection: handleMoreEditOptionsSelection,
        };

        const chartProps = {
            ...baseComponentProps,
            maxWidthHeightRatio: chartMaxWidthHeightRatio,
            // TODO: Future enhancement to store user selected contracts in the dashboard widget options.
            selectedContracts: props.dashboardWidgetPageElement.options?.selectedContracts,
        };

        switch (props.dashboardWidgetPageElement.pageElement.pageElementType) {
            case DemeterPageElementType.ProjectionChart: {
                const ProjectionChartFromPageElement = withProjectionChartFromPageElement(ProjectionChart);
                return <ProjectionChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'ProjectionChart' }} />;
            }
            case DemeterPageElementType.SeasonalChart: {
                const SeasonalChartFromPageElement = withSeasonalChartFromPageElement(SeasonalChart);
                return <SeasonalChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'SeasonalChart' }} />;
            }
            case DemeterPageElementType.FuturesPricesChart: {
                const FuturesPricesChartFromPageElement = withFuturesPricesChartFromPageElement(FuturesPricesChart);
                return <FuturesPricesChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'FuturesPricesChart' }} />;
            }
            case DemeterPageElementType.OtcPricesChart: {
                const OtcPricesChartFromPageElement = withOtcPricesChartFromPageElement(OtcPricesChart);
                return <OtcPricesChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'OtcPricesChart' }} />;
            }
            case DemeterPageElementType.FuturesPricesComparisonChart: {
                const FuturesPricesComparisonChartFromPageElement = withFuturesPricesComparisonChartFromPageElement(FuturesPricesComparisonChart);
                return (
                    <FuturesPricesComparisonChartFromPageElement
                        {...chartProps}
                        track={{ eventCategory: 'Dashboard', eventAction: 'FuturesPricesComparisonChart' }}
                    />
                );
            }
            case DemeterPageElementType.FuturesPricesForwardCurveChart: {
                const FuturesPricesForwardCurveChartFromPageElement = withFuturesPricesForwardCurveChartFromPageElement(FuturesPricesForwardCurveChart);
                return (
                    <FuturesPricesForwardCurveChartFromPageElement
                        {...chartProps}
                        track={{ eventCategory: 'Dashboard', eventAction: 'FuturesPricesForwardCurveChart' }}
                    />
                );
            }
            case DemeterPageElementType.PhysicalPricesChart: {
                const PhysicalPricesChartFromPageElement = withPhysicalPricesChartFromPageElement(PhysicalPricesChart);
                return <PhysicalPricesChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'PhysicalPricesChart' }} />;
            }
            case DemeterPageElementType.MarginChart: {
                const MarginChartFromPageElement = withMarginChartFromPageElement(ProjectionChart);
                return <MarginChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'MarginChart' }} />;
            }
            case DemeterPageElementType.BalanceTableChart: {
                const BalanceTableChartFromPageElement = withBalanceTableChartFromPageElement(BalanceTableChart);
                return <BalanceTableChartFromPageElement {...chartProps} track={{ eventCategory: 'Dashboard', eventAction: 'BalanceTableChart' }} />;
            }
            case DemeterPageElementType.FuturesPricesTable: {
                const FuturesPricesTableFromPageElement = withFuturesPricesTableFromPageElement(DashboardPricesTable);
                return <FuturesPricesTableFromPageElement {...baseComponentProps} track={{ eventCategory: 'Dashboard', eventAction: 'FuturesPricesTable' }} />;
            }
            case DemeterPageElementType.OtcPricesTable: {
                const OtcPricesTableFromPageElement = withOtcPricesTableFromPageElement(DashboardPricesTable);
                return <OtcPricesTableFromPageElement {...baseComponentProps} track={{ eventCategory: 'Dashboard', eventAction: 'OtcPricesTable' }} />;
            }
            case DemeterPageElementType.PhysicalPricesTable: {
                const PhysicalPricesTableFromPageElement = withPhysicalPricesTableFromPageElement(DashboardPricesTable);
                return (
                    <PhysicalPricesTableFromPageElement {...baseComponentProps} track={{ eventCategory: 'Dashboard', eventAction: 'PhysicalPricesTable' }} />
                );
            }

            case DemeterPageElementType.PriceSummaries:
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="PriceSummaries"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                    >
                        <CustomDashboardPriceSummaries />
                    </DashboardNonChartWrapper>
                );
            case DemeterPageElementType.MarketIntelligenceReports:
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="DashboardMarketIntelligenceReports"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                    >
                        <CustomDashboardMarketIntelligenceReports />
                    </DashboardNonChartWrapper>
                );
            case DemeterPageElementType.LeadingIndicators: {
                const LeadingIndicatorsFromPageElement = withLeadingIndicatorsFromPageElement(CustomDashboardIndicators);
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="DashboardLeadingIndicators"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                    >
                        <LeadingIndicatorsFromPageElement track={{ eventCategory: 'Dashboard', eventAction: 'LeadingIndicators' }} />
                    </DashboardNonChartWrapper>
                );
            }
            case DemeterPageElementType.HedgeMonitorPro:
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="CustomDashboardHedgeMonitorProIndicators"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                        titleWithTooltip={translations.hedgeMonitorPro.tooltipMessage}
                    >
                        <CustomDashboardHedgeMonitorProIndicators track={{ eventCategory: 'Dashboard', eventAction: 'HedgeMonitorPro' }} />
                    </DashboardNonChartWrapper>
                );
            case DemeterPageElementType.CommoditySummaryTable:
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="DashboardCommoditySummaryTable"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                    >
                        <CustomDashboardCommoditySummaryTable dashboardWidget={props.dashboardWidget} />
                    </DashboardNonChartWrapper>
                );
            case DemeterPageElementType.CommodityIndicators:
                return (
                    <DashboardNonChartWrapper
                        {...baseComponentProps}
                        name="DashboardMarketIndicators"
                        title={translations.pageElementType[props.dashboardWidgetPageElement.pageElement.pageElementType]}
                        titleWithTooltip={translations.marketIndicators.tooltipMessage}
                    >
                        <CustomDashboardMarketIndicators />
                    </DashboardNonChartWrapper>
                );
            default:
                return <> </>;
        }
    }, [props.dashboardWidgetPageElement, props.isInEditMode]);

    const containerClassName = shouldUseDynamicHeight ? styles.dragAndDrop_generated_component_dynamic_height : styles.dragAndDrop_generated_component;

    return (
        <div ref={dragAndDropContext.dragProps.reference} style={dragAndDropContext.dragProps.style} className={containerClassName}>
            {switchOnPageElementType}
        </div>
    );
};

export default GenerateComponentFromPageElement;
